/**
 * Predi 链 / 代币注册表（单一真源）
 * ============================================================================
 * 目标：让「接入更多 EVM 链」与「随时增加 ERC20 代币」都只需要改这一个文件。
 *
 * 新增一条链：
 *   1) 在 CHAIN_REGISTRY 里追加一个 key（例如 'arbitrum'），照抄 bsc 的结构改值；
 *   2) 若该链需要默认开启，把它加入 .env 的 VUE_APP_CHAINS（逗号分隔）；
 *   3) 合约地址填 contracts 下的字段；未部署的合约留空字符串即可。
 *
 * 新增一个 ERC20 代币：
 *   在对应链的 tokens 数组里追加一项即可（role 取值见下方注释）。
 *   第一个 role === 'pay' 的代币会成为默认支付代币（兼容旧的 payToken）。
 *   第一个 role === 'points' 的代币会成为积分代币（兼容旧的 ferc20）。
 *
 * ⚠️ 合约地址是**部署产物**，不是常量：每次部署/升级都会变。
 *    因此地址优先从 `.env` 读取（见下方 CONTRACT_ENV_FIELDS），本文件里的字面量
 *    只是**兜底默认值**。部署后请更新 `dapp/.env` 与 `backend/.env`，不要改这个文件。
 *    可用 `node solidity-contract/scripts/exportEnv.js <network>` 打印出可粘贴的配置。
 *
 * 注意：本文件只描述「前端需要知道」的链与代币元数据，不含任何私钥。
 * 后端对应的链/代币配置在 backend（config/blockchain.php + chain_assets 表）。
 */

/** 常用代币预设，避免同一条链上重复填写地址 */
export const TOKEN_PRESETS = {
  BSC_USDT:   { symbol: 'USDT', decimals: 18, icon: '/images/usdt.png' },
  BSC_PRDS:   { symbol: 'PRDS', decimals: 18, icon: '/images/FERC20.jpg' },
  ETH_USDT:   { symbol: 'USDT', decimals: 6,  icon: '/images/usdt.png' },
};

/**
 * 链注册表。
 * @typedef {Object} ChainConfig
 * @property {string}  key                唯一标识
 * @property {number}  chainId            EVM chainId（十进制）
 * @property {string}  chainType          后端 Chain 枚举（'1' = BSC 主网）
 * @property {string}  chainName          展示名
 * @property {string}  chainNameMobile    移动端展示名
 * @property {string}  icon               图标名
 * @property {string[]} rpcUrls           RPC 列表
 * @property {string[]} blockExplorerUrls 区块浏览器
 * @property {Object}  nativeCurrency     原生币
 * @property {string}  explorer           浏览器根地址
 * @property {Object}  contracts          合约地址（未部署留空）
 * @property {Array}   tokens             ERC20 代币列表
 * @property {boolean} enabled            是否在前端启用
 * @property {boolean} [isDefault]        是否为默认链
 */
export const CHAIN_REGISTRY = {
  bsc: {
    key: 'bsc',
    chainId: 56,
    chainType: '1',
    // 后端 Chain 枚举名（签名消息里的 chain 字段必须用它，不能用展示名）
    chainEnum: 'BSC',
    chainName: 'BSC',
    chainNameMobile: 'BSC',
    icon: 'BNB',
    // 多节点：读请求走 FallbackProvider 自动切；钱包 429 时也靠这个列表轮换
    rpcUrls: [
      'https://bsc-dataseed.binance.org',
      'https://bsc-dataseed1.defibit.io',
      'https://bsc-dataseed1.ninicoin.io',
    ],
    blockExplorerUrls: ['https://bscscan.com'],
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    explorer: 'https://bscscan.com',
    contracts: {
      ticket: '0xc4F3b652917e9B8A12c96B2990b5C5e8531C3440',
      ticketSale: '0x033956F3d53c72f24C11bDC38C88177fBDEA0B4E',
      ticketMarket: '',
      vault: '0xB6390e82Ab79950E9e4Cc6e9f1131a322Fb87015',
      // v2.4.0：Price 直投合约（未部署前留空 = 该链不支持直投模式）
      priceDirect: '',
      bridge: '0x8B20023accFC177D05E1c28157cEda9e49Fc51f1',
      prdsClaim: '0x3cd8170ea2620fc57bf67d9eaee9af470a897cd6',
    },
    tokens: [
      { role: 'points', address: '0xfDEe9CD7090f8A30fa6Df9De3871A94392f6b10b', ...TOKEN_PRESETS.BSC_PRDS },
      { role: 'pay',    address: '0x55d398326f99059ff775485246999027b3197955', ...TOKEN_PRESETS.BSC_USDT },
    ],
    enabled: true,
    isDefault: true,
  },

  // BSC 测试网（chainId 97）：默认不启用，用 VUE_APP_CHAINS=bsc,bscTest 打开。
  // 地址来自 solidity-contract/network/bscTest/contracts.json（2026-10-15 部署），
  // 重新部署后请用 `node solidity-contract/scripts/exportEnv.js bscTest` 回填 dapp/.env。
  bscTest: {
    key: 'bscTest',
    chainId: 97,
    chainType: '1',
    chainEnum: 'BSC',
    chainName: 'BSC Testnet',
    chainNameMobile: 'BSC T',
    icon: 'BNB',
    // 公共测试网节点普遍有速率限制（429）。多备几条：
    // 读请求由 sdk/rpc.js 的 FallbackProvider 自动切；钱包被迫 429 时也会轮换到这里。
    rpcUrls: [
      'https://bsc-testnet.publicnode.com',
      'https://bsc-testnet-rpc.publicnode.com',
      'https://bsc-testnet.drpc.org',
    ],
    blockExplorerUrls: ['https://testnet.bscscan.com'],
    nativeCurrency: { name: 'tBNB', symbol: 'tBNB', decimals: 18 },
    explorer: 'https://testnet.bscscan.com',
    contracts: {
      ticket: '0x90D920b6D8654612CA13BB6fAb55aA5747cD4B50',
      ticketSale: '0x2b1D247D39969faa3CEbAf215d1CCA0FF714B178',
      ticketMarket: '0xC8A971d340A622d2A919a0D6813cfD87a9746e97',
      vault: '0x7751ec8DBc931d4aF1c36d022F561918105Dc6B0',
      // v2.4.0 直投合约（2026-11-01 部署，见 docs/CONTRACT_ADDRESSES.md）
      priceDirect: '0xB56aba3cA02d21807c8952b68E7092722b70F7d5',
      bridge: '',
      prdsClaim: '',
    },
    tokens: [
      { role: 'points', address: '0x7E8716d33B474bD0bE1A173987Ba6eAea746642d', ...TOKEN_PRESETS.BSC_PRDS },
      // 测试网没有真实 USDT，这里用的是 scripts/deployTestUsdt.ts 发出的测试代币
      { role: 'pay', address: '0x63a181F1cB9e7E3C8926Eb0A6b2B983a706AB585', ...TOKEN_PRESETS.BSC_USDT },
    ],
    enabled: false,
  },

  // BOT Chain（chainId 677 / 0x2a5，主链币 BOT）—— 2026-10 接入。
  // ⚠️ 合约尚未部署：contracts 与 tokens 全空，参与类能力等部署完成后回填
  //   （solidity-contract 部署 → exportEnv.js bot → dapp/.env 的 VUE_APP_*_BOT）。
  // 当前仅做「可见」：链选择器、/api/chains、后台链注册表与 RPC 页可见。
  bot: {
    key: 'bot',
    chainId: 677,
    chainType: '7',
    chainEnum: 'BOT',
    chainName: 'BOT Chain',
    chainNameMobile: 'BOT',
    icon: 'BOT',
    rpcUrls: [
      'https://rpc.botchain.ai',
    ],
    blockExplorerUrls: [],
    nativeCurrency: { name: 'BOT', symbol: 'BOT', decimals: 18 },
    explorer: '',
    contracts: {
      ticket: '',
      ticketSale: '',
      ticketMarket: '',
      vault: '',
      priceDirect: '',
      bridge: '',
      prdsClaim: '',
    },
    tokens: [],
    enabled: true,
  },
};

/* ===========================================================================
 * 合约地址的「部署产物」注入
 * ---------------------------------------------------------------------------
 * 部署脚本（scripts/deploy.ts / deployMarket.ts）会把新地址写回
 * solidity-contract/network/<net>/contracts.json。请把地址同步到 .env：
 *
 *   backend/.env   VAULT / TICKET / TICKET_SALE / TICKET_MARKET / PRDS / USDT ...
 *   dapp/.env      下面的 VUE_APP_*（本文件读取它们并覆盖注册表里的字面量）
 *
 * 命名规则（后缀 = 链 key 的大写；**默认链不加后缀**）：
 *   默认链（VUE_APP_DEFAULT_CHAIN）  VUE_APP_TICKET / VUE_APP_VAULT / ...
 *   其它链                            VUE_APP_TICKET_ARB / VUE_APP_VAULT_ARB / ...
 *
 * 留空 = 沿用本文件里的兜底默认值，所以「不配 .env」的行为与以前完全一致。
 * =========================================================================== */

/** 环境变量名（不含后缀）-> contracts 字段名 */
export const CONTRACT_ENV_FIELDS = {
  VUE_APP_TICKET: 'ticket',
  VUE_APP_TICKET_SALE: 'ticketSale',
  VUE_APP_TICKET_MARKET: 'ticketMarket',
  VUE_APP_VAULT: 'vault',
  VUE_APP_PRICE_DIRECT: 'priceDirect',
  VUE_APP_BRIDGE: 'bridge',
  VUE_APP_PRDS_CLAIM: 'prdsClaim',
};

/** 环境变量名（不含后缀）-> tokens 里的 role */
export const TOKEN_ENV_ROLES = {
  VUE_APP_PAY_TOKEN: 'pay',
  VUE_APP_POINTS_TOKEN: 'points',
};

function envValue(name) {
  const v = process.env[name];
  return typeof v === 'string' ? v.trim() : '';
}

/** 默认链的 key：VUE_APP_DEFAULT_CHAIN > isDefault > 第一个 enabled > 第一个 */
export function resolveDefaultChainKey() {
  const wanted = envValue('VUE_APP_DEFAULT_CHAIN');
  if (wanted && CHAIN_REGISTRY[wanted]) {
    return wanted;
  }
  const list = Object.values(CHAIN_REGISTRY);
  const byFlag = list.find((c) => c.isDefault);
  if (byFlag) {
    return byFlag.key;
  }
  const byEnabled = list.find((c) => c.enabled);
  return (byEnabled || list[0] || {}).key || '';
}

export const defaultChainKey = resolveDefaultChainKey();

/**
 * 用 .env 覆盖某条链的合约地址与代币地址（仅在配置了非空值时覆盖）。
 */
function applyEnvOverrides(chain) {
  const suffix = chain.key === defaultChainKey ? '' : '_' + String(chain.key).toUpperCase();

  chain.contracts = chain.contracts || {};
  Object.keys(CONTRACT_ENV_FIELDS).forEach((envName) => {
    const value = envValue(envName + suffix);
    if (value) {
      chain.contracts[CONTRACT_ENV_FIELDS[envName]] = value;
    }
  });

  chain.tokens = chain.tokens || [];
  Object.keys(TOKEN_ENV_ROLES).forEach((envName) => {
    const value = envValue(envName + suffix);
    if (!value) {
      return;
    }
    const role = TOKEN_ENV_ROLES[envName];
    const token = chain.tokens.find((t) => t.role === role);
    if (token) {
      token.address = value;
    } else {
      chain.tokens.push({ role, address: value, symbol: role.toUpperCase(), decimals: 18 });
    }
  });
}

Object.values(CHAIN_REGISTRY).forEach(applyEnvOverrides);

/** 十进制 chainId -> 十六进制字符串 */
export function toHexChainId(chainId) {
  return '0x' + Number(chainId).toString(16);
}

/** 十六进制字符串 -> 十进制 */
export function toDecChainId(hexChainId) {
  return Number(hexChainId);
}

/** 取某条链的默认支付代币（兼容旧 payToken） */
export function payTokenOf(chain) {
  const t = (chain.tokens || []).find((x) => x.role === 'pay') || chain.tokens[0] || {};
  return { name: t.symbol, address: t.address, decimals: t.decimals, icon: t.icon };
}

/** 取某条链的积分代币（兼容旧 ferc20） */
export function pointsTokenOf(chain) {
  const t = (chain.tokens || []).find((x) => x.role === 'points') || {};
  return { name: t.symbol, address: t.address, decimals: t.decimals, icon: t.icon };
}

/**
 * 把注册表转换为历史沿用的 Network 结构（以十六进制 chainId 为 key）。
 * 这样既支持多链，又完全兼容既有组件对 `Network[chainId]` 的读取。
 */
export function buildNetworkMap(chains) {
  const map = {};
  chains.forEach((chain) => {
    const hex = toHexChainId(chain.chainId);
    map[hex] = {
      chainId: hex,
      chainType: chain.chainType,
      // 与后端 App\Enums\Chain 的 case 名一致：签名消息里用这个，展示用 chainName
      chainEnum: chain.chainEnum || 'BSC',
      chainName: chain.chainName,
      chainNameMobile: chain.chainNameMobile,
      icon: chain.icon,
      rpcUrls: chain.rpcUrls,
      blockExplorerUrls: chain.blockExplorerUrls,
      nativeCurrency: chain.nativeCurrency,
      // 新增：完整的代币列表，供"随时增加 ERC20"使用
      tokens: chain.tokens || [],
      // 兼容字段
      ferc20: pointsTokenOf(chain),
      payToken: payTokenOf(chain),
    };
  });
  return map;
}
